<script setup lang="ts">
import { onMounted, type Ref, ref, computed } from 'vue';
import cloneDeep from 'lodash.clonedeep';
import { AxiosError } from 'axios';
import { RBtn } from '@therobotcompany/robco-frontend-library';
import gettext from '@/i18n/gettext';
import { type Cell, EMPTY_NEW_CELL, type NewCell } from '@/models/Cell';
import {
  loadCells,
  createCell,
  patchCell,
  deleteCell,
} from '@/api/AdminCellApi';
import { requiredRule } from '@/helpers/FormRules';
import useAuthStore from '@/store/AuthStore';
import { Permission } from '@/models/Roles';
import { useAlertsStore } from '@/store/AlertsStore';

const props = defineProps<{
  customerUuid: string;
  customerName: string;
}>();

const { $gettext } = gettext;
const authStore = useAuthStore();
const alerts = useAlertsStore();

const cells: Ref<Cell[]> = ref([]);
const isLoading = ref(true);
const editedCell: Ref<Cell | null> = ref(null);
const cellToDelete: Ref<Cell | null> = ref(null);
const editDialog = ref(false);
const createDialog = ref(false);
const deleteDialog = ref(false);
const newCell: Ref<NewCell> = ref({
  ...cloneDeep(EMPTY_NEW_CELL),
  customerUuid: props.customerUuid,
});

const createFormValid = ref(false);
const editFormValid = ref(true);

const cellsForCustomer = computed(() =>
  cells.value.filter((c) => c.customerUuid === props.customerUuid),
);

async function fetchCells() {
  isLoading.value = true;
  try {
    cells.value = await loadCells();
  } catch (err) {
    alerts.error($gettext('Failed to load cells'));
    console.error(err);
  } finally {
    isLoading.value = false;
  }
}

function openCreateDialog() {
  newCell.value = {
    ...cloneDeep(EMPTY_NEW_CELL),
    customerUuid: props.customerUuid,
  };
  createDialog.value = true;
}

function closeCreateDialog() {
  createDialog.value = false;
}

async function submitCreateCell() {
  try {
    await createCell(newCell.value);
    alerts.success($gettext('Cell created'));
    fetchCells();
    closeCreateDialog();
  } catch (err) {
    alerts.error($gettext('Failed to create cell'));
    console.error(err);
  }
}

function enableEdit(cell: Cell) {
  editedCell.value = cloneDeep(cell);
  editDialog.value = true;
}

function closeEditDialog() {
  editedCell.value = null;
  editDialog.value = false;
}

async function saveChanges() {
  if (!editedCell.value) {
    return;
  }
  try {
    await patchCell(editedCell.value);
    alerts.success($gettext('Cell updated'));
    fetchCells();
    closeEditDialog();
  } catch (err) {
    if (err instanceof AxiosError && err.response?.status === 404) {
      alerts.error($gettext('Cell not found'));
    } else {
      alerts.error($gettext('Failed to update cell'));
    }
    console.error(err);
  }
}

function enableDelete(cell: Cell) {
  cellToDelete.value = cell;
  deleteDialog.value = true;
}

function closeDeleteDialog() {
  cellToDelete.value = null;
  deleteDialog.value = false;
}

async function confirmDelete() {
  if (!cellToDelete.value) {
    return;
  }
  try {
    await deleteCell(cellToDelete.value);
    alerts.success($gettext('Cell deleted'));
    fetchCells();
    closeDeleteDialog();
  } catch (err) {
    if (err instanceof AxiosError && err.response?.status === 409) {
      alerts.error(
        $gettext(
          'Cannot delete cell: control units are still assigned. Reassign or delete them first.',
        ),
      );
    } else {
      alerts.error($gettext('Failed to delete cell'));
    }
    console.error(err);
  }
}

onMounted(() => {
  fetchCells();
});
</script>

<template>
  <Transition appear name="cells-expand">
    <div class="pa-4">
      <div class="d-flex align-center mb-4">
        <h3>{{ customerName }} {{ $gettext('Cells') }}</h3>
        <v-spacer />
        <r-btn
          v-if="authStore.hasPermission(Permission.POST_CUSTOMER)"
          size="small"
          variant="outlined"
          @click="openCreateDialog"
        >
          {{ $gettext('Add Cell') }}
        </r-btn>
      </div>

      <div
        v-if="isLoading"
        class="d-flex flex-column align-center justify-center pa-6"
      >
        <v-progress-circular indeterminate color="primary" />
        <p class="text-medium-emphasis text-body-2 mt-3">
          {{ $gettext('Loading cells…') }}
        </p>
      </div>
      <p
        v-else-if="cellsForCustomer.length === 0"
        class="text-medium-emphasis text-body-2"
      >
        {{ $gettext('No cells yet for this customer.') }}
      </p>
      <v-table v-else density="compact">
        <thead>
          <tr>
            <th>{{ $gettext('Name') }}</th>
            <th>{{ $gettext('Internal Name') }}</th>
            <th
              v-if="
                authStore.hasPermission(Permission.PATCH_CUSTOMER) ||
                authStore.hasPermission(Permission.DELETE_CUSTOMER)
              "
              style="width: 120px"
            ></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="cell in cellsForCustomer" :key="cell.uuid">
            <td>{{ cell.name }}</td>
            <td>{{ cell.internalName }}</td>
            <td>
              <v-icon
                v-if="authStore.hasPermission(Permission.PATCH_CUSTOMER)"
                icon="mdi-pencil"
                size="small"
                class="mr-2"
                @click.stop="enableEdit(cell)"
              />
              <v-icon
                v-if="authStore.hasPermission(Permission.DELETE_CUSTOMER)"
                icon="mdi-trash-can-outline"
                size="small"
                @click.stop="enableDelete(cell)"
              />
            </td>
          </tr>
        </tbody>
      </v-table>

      <v-dialog v-model="createDialog" max-width="600">
        <v-card>
          <v-card-title>
            {{ $gettext('Add Cell') }} —
            <span class="text-medium-emphasis">{{ customerName }}</span>
          </v-card-title>
          <v-card-text>
            <v-form v-model="createFormValid" @submit.prevent>
              <v-text-field
                v-model="newCell.name"
                :label="$gettext('Name')"
                :rules="[requiredRule]"
              />
              <v-text-field
                v-model="newCell.internalName"
                :label="$gettext('Internal Name')"
              />
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-btn @click="closeCreateDialog">{{ $gettext('Close') }}</v-btn>
            <v-spacer />
            <v-btn
              color="primary"
              :disabled="!createFormValid"
              @click="submitCreateCell"
              >{{ $gettext('Add') }}</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog
        v-if="editedCell"
        v-model="editDialog"
        max-width="600"
        @click:outside="closeEditDialog"
      >
        <v-card>
          <v-card-title>{{ $gettext('Cell') }}</v-card-title>
          <v-card-text>
            <v-form v-model="editFormValid" @submit.prevent>
              <v-text-field
                v-model="editedCell.name"
                :label="$gettext('Name')"
                :rules="[requiredRule]"
              />
              <v-text-field
                v-model="editedCell.internalName"
                :label="$gettext('Internal Name')"
              />
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-btn @click="closeEditDialog">{{ $gettext('Close') }}</v-btn>
            <v-spacer />
            <v-btn
              color="primary"
              :disabled="!editFormValid"
              @click="saveChanges"
              >{{ $gettext('Save') }}</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="deleteDialog" max-width="600px">
        <v-card>
          <v-card-title>{{ $gettext('Confirm Deletion') }}</v-card-title>
          <v-card-text>
            <p>{{ $gettext('Are you sure you want to delete this cell?') }}</p>
            <p>
              <strong>{{ cellToDelete?.name }}</strong>
            </p>
          </v-card-text>
          <v-card-actions>
            <v-btn color="primary" variant="text" @click="closeDeleteDialog">{{
              $gettext('Cancel')
            }}</v-btn>
            <v-spacer />
            <v-btn color="error" variant="text" @click="confirmDelete">{{
              $gettext('Delete')
            }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </Transition>
</template>

<style scoped>
.cells-expand-enter-active {
  transition:
    opacity 0.25s ease-out,
    transform 0.25s ease-out;
}
.cells-expand-enter-from {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
